import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { IWorkState } from ".";
import { now } from "../../utils/time";
import { ITicketsBalances, IUserBalances } from "../../model/user-info.interface";
import { IUserInfoState } from "../user";

/** Срез ответа convert-energy: баланс может включать зарплату и другие начисления за тот же запрос */
export type IConvertEnergyUpdatedBalance = Partial<IUserBalances>;

export type WorkProfits = Partial<IUserBalances>;

export interface IConvertEnergyResponse {
    profits: WorkProfits;
    exp: number;
    updatedBalance?: IConvertEnergyUpdatedBalance;
}

/** Ответ API + баланс до запроса (для анимации; updatedBalance уже применён интерцептором) */
export type IConvertEnergyThunkResult = IConvertEnergyResponse & {
    balanceBeforeConvert: number;
};

type PickUserBalance = { user: { info: { balance: number } } };

export const convertEnergy = createAsyncThunk<IConvertEnergyThunkResult>(
    'work/convertEnergy',
    async (_, { getState }) => {
        const balanceBeforeConvert = (getState() as PickUserBalance).user.info.balance;
        const data = (await api.work.convertEnergy()) as IConvertEnergyResponse;
        return { ...data, balanceBeforeConvert };
    }
);

const convertEnegryHandler = (builder: ActionReducerMapBuilder<IWorkState>) => {
    builder
        .addCase(convertEnergy.pending, (state: IWorkState) => {
            state.error = "";
        })
        .addCase(convertEnergy.fulfilled, (state: IWorkState, action: PayloadAction<IConvertEnergyThunkResult>) => {
            state.lastConvertDate = now();

            if (!action.payload)
                return;

            const before = action.payload.balanceBeforeConvert;
            const serverAfter = action.payload.updatedBalance?.balance;

            if (serverAfter !== undefined) {
                state.profit += serverAfter - before;
                state.balanceAnimationTarget = serverAfter;
            }
            else {
                state.profit += action.payload.profits.balance ?? 0;
                state.balanceAnimationTarget = null;
            }

            state.balanceAnimationBase = before;
        })
        .addCase(convertEnergy.rejected, (state: IWorkState) => {
            state.error = 'Unable to convert energy';
            state.balanceAnimationBase = null;
            state.balanceAnimationTarget = null;
        });
};

const TICKET_BALANCE_KEYS: Array<keyof ITicketsBalances> = [
    "regularTickets",
    "rareTickets",
    "epicTickets",
    "legendaryTickets",
];

const updatedBalanceHasTickets = (updatedBalance?: IConvertEnergyUpdatedBalance): boolean =>
    Boolean(
        updatedBalance &&
            TICKET_BALANCE_KEYS.some((key) => updatedBalance[key] !== undefined)
    );

export const convertEnergyFulfilledHandler = (state: IUserInfoState, action: PayloadAction<IConvertEnergyThunkResult>) => {
    if (!action.payload)
        return;

    if (action.payload.exp)
        state.exp = action.payload.exp;

    // updatedBalance (работа + испытание) уже применён axios-интерцептором как абсолютные значения
    if (updatedBalanceHasTickets(action.payload.updatedBalance))
        return;

    const ticketProfits = action.payload.profits;
    if (!ticketProfits)
        return;

    TICKET_BALANCE_KEYS.forEach((ticket) => {
        if (ticketProfits[ticket] !== undefined)
            state.tickets[ticket] += ticketProfits[ticket]!;
    });
};

export default convertEnegryHandler;
